import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { detectTargetType } from "@/lib/intel/detect";

export const MONITOR_SIGNAL_KEYS = [
  "breach", "company_role", "sanctions", "lawsuit", "new_domain", "github_leak", "media",
] as const;

export type MonitorDTO = {
  id: string;
  target: string;
  targetType: string;
  label: string | null;
  signals: string[];
  cadenceHours: number;
  enabled: boolean;
  lastCheckedAt: string | null;
  lastStatus: string | null;
  nextCheckAt: string;
  createdAt: string;
  eventCount?: number;
};

export type MonitorEventDTO = {
  id: string;
  monitorId: string;
  signal: string;
  title: string;
  detail: string | null;
  severity: string;
  ref: string | null;
  createdAt: string;
  target?: string;
};

/** Watchlist size per plan — keeps recurring collector cost bounded. */
const PLAN_CAP: Record<string, number> = {
  operator_plan: 3,
  analyst_plan: 25,
  command_plan: 200,
};

async function planCap(supabase: any, userId: string): Promise<number> {
  const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (isAdmin) return 1000;
  for (const env of ["live", "sandbox"]) {
    const { data: tier } = await supabase.rpc("current_tier", { user_uuid: userId, check_env: env });
    if (tier && PLAN_CAP[tier as string]) return PLAN_CAP[tier as string];
  }
  return 0;
}

export const listMonitors = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ monitors: MonitorDTO[]; cap: number }> => {
    const { supabase, userId } = context;
    const [{ data, error }, cap] = await Promise.all([
      supabase
        .from("monitors")
        .select("id,target,target_type,label,signals,cadence_hours,enabled,last_checked_at,last_status,next_check_at,created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false }),
      planCap(supabase, userId),
    ]);
    if (error) throw new Error(error.message);
    return {
      cap,
      monitors: ((data ?? []) as any[]).map((r) => ({
        id: r.id, target: r.target, targetType: r.target_type, label: r.label,
        signals: r.signals ?? [], cadenceHours: r.cadence_hours, enabled: r.enabled,
        lastCheckedAt: r.last_checked_at, lastStatus: r.last_status,
        nextCheckAt: r.next_check_at, createdAt: r.created_at,
      })),
    };
  });

export const listMonitorEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MonitorEventDTO[]> => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("monitor_events")
      .select("id,monitor_id,signal,title,detail,severity,ref,created_at,monitors(target)")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return ((data ?? []) as any[]).map((r) => ({
      id: r.id, monitorId: r.monitor_id, signal: r.signal, title: r.title,
      detail: r.detail, severity: r.severity, ref: r.ref, createdAt: r.created_at,
      target: r.monitors?.target,
    }));
  });

export const createMonitor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      target: z.string().trim().min(2).max(180),
      targetType: z.string().trim().max(20).optional(),
      label: z.string().trim().max(120).optional(),
      signals: z.array(z.enum(MONITOR_SIGNAL_KEYS)).max(7).optional(),
      cadenceHours: z.number().int().min(6).max(168).optional(),
      workspace: z.string().trim().max(60).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const cap = await planCap(supabase, userId);
    if (cap === 0) {
      throw new Error("Live monitoring requires an active plan. Upgrade to start watching targets.");
    }
    const { count } = await supabase
      .from("monitors")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    if ((count ?? 0) >= cap) {
      throw new Error(`Watchlist limit reached (${cap}). Upgrade your plan to monitor more targets.`);
    }
    const targetType = data.targetType || detectTargetType(data.target);
    const { error } = await supabase.from("monitors").insert({
      user_id: userId,
      target: data.target,
      target_type: targetType,
      label: data.label ?? null,
      signals: data.signals ?? [],
      cadence_hours: data.cadenceHours ?? 24,
      workspace: data.workspace ?? null,
    });
    if (error) {
      if (/duplicate key/i.test(error.message)) throw new Error("You are already monitoring that target.");
      throw new Error(error.message);
    }
    return { ok: true, targetType };
  });

export const setMonitorEnabled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid(), enabled: z.boolean() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("monitors")
      .update({ enabled: data.enabled })
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteMonitor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("monitors")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Manual re-check. Rate-limited to once per hour per monitor to protect cost. */
export const runMonitorNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase
      .from("monitors")
      .select("id,user_id,target,target_type,signals,cadence_hours,fingerprint,last_checked_at")
      .eq("id", data.id)
      .eq("user_id", userId)
      .single();
    if (error || !row) throw new Error("Monitor not found");
    const last = (row as any).last_checked_at ? Date.parse((row as any).last_checked_at) : 0;
    if (Date.now() - last < 3600_000) {
      throw new Error("This monitor was checked less than an hour ago. Try again later.");
    }
    const { checkMonitor } = await import("@/lib/intel/monitor.server");
    const res = await checkMonitor(row as any);
    return { ok: true, ...res };
  });